/*
 * BuyGuardPreview — iter-215 P1
 * ------------------------------
 * React counterpart of the Shopify-theme Buy Guard (aniyor-buy-guard-v1.js).
 * Reads from the same SSOT endpoint /api/serviceability/listing/{id}/checkout-config
 * so the vendor preview matches *exactly* what a Shopify customer sees.
 *
 * Two surfaces, per user choice iter-215 Phase 2:
 *   • <BuyGuardDecisions listingId={id} /> — right column, below the
 *     existing price/variant picker. Shows locality picker, day chips,
 *     pincode check, lead-time, Tier-2 state check, other dynamic
 *     decision fields.
 *   • <BuyGuardInfoTabs listingId={id} /> — left column, below the image
 *     gallery. Tabs for inclusions, exclusions, shipping/returns,
 *     policies using the vendor_value each field carries.
 *
 * The component gracefully degrades: if the endpoint returns 404 (listing
 * never synced), renders nothing.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { MapPin, Clock, Calendar, Truck, Shield, AlertCircle, Eye } from 'lucide-react';
import { IconByKey } from './IconTagInput';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Shared config fetch — one request powers both left and right surfaces.
// We memoise per listingId via a module-level cache so the two components
// don't double-fetch.
// ---------------------------------------------------------------------------
const _cache = new Map();
async function fetchCheckoutConfig(listingId) {
  if (_cache.has(listingId)) return _cache.get(listingId);
  const p = fetch(`${BACKEND}/api/serviceability/listing/${listingId}/checkout-config`)
    .then(r => (r.ok ? r.json() : null))
    .catch(() => null);
  _cache.set(listingId, p);
  return p;
}
export const clearBuyGuardCache = (id) => id ? _cache.delete(id) : _cache.clear();

// ---------------------------------------------------------------------------
// Small atoms
// ---------------------------------------------------------------------------
const Row = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-b last:border-0" data-testid={testid}>
    <span className="text-sm text-gray-600 font-medium">{label}</span>
    <div className="text-sm text-right max-w-[60%]">{children}</div>
  </div>
);

const Chips = ({ values }) => (
  <div className="flex flex-wrap gap-1 justify-end">
    {values.map((v, i) => (
      <Badge key={i} variant="secondary" className="text-xs">{v}</Badge>
    ))}
  </div>
);

const renderFieldValue = (f) => {
  const v = f.vendor_value;
  switch (f.ui) {
    case 'bullet_list':
      if (Array.isArray(v) && v.length) {
        return (
          <ul className="list-none pl-0 text-left space-y-1">
            {v.map((it, i) => {
              const isObj = it && typeof it === 'object' && !Array.isArray(it);
              const label = isObj ? (it.label || it.text || JSON.stringify(it)) : String(it);
              const iconKey = isObj ? it.icon : null;
              return (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  {iconKey
                    ? <IconByKey iconKey={iconKey} className="h-4 w-4 mt-0.5 text-indigo-600 flex-shrink-0" />
                    : <span className="h-4 w-4 mt-0.5 flex-shrink-0 flex items-center justify-center">
                        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                      </span>}
                  <span>{label}</span>
                </li>
              );
            })}
          </ul>
        );
      }
      if (typeof v === 'string' && v.trim()) {
        const items = v.split(/[\n,]/).map(x => x.trim()).filter(Boolean);
        return (
          <ul className="list-disc list-outside pl-4 text-left space-y-0.5">
            {items.map((it, i) => <li key={i} className="text-sm text-gray-700">{it}</li>)}
          </ul>
        );
      }
      return <span className="text-gray-400">—</span>;
    case 'chips':
    case 'day_chips':
      return Array.isArray(v) && v.length ? <Chips values={v} /> : <span className="text-gray-400">—</span>;
    case 'boolean_badge':
      return <Badge className={v ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>{v ? 'Yes' : 'No'}</Badge>;
    case 'currency_badge':
      return v != null ? <Badge variant="outline">₹{v}</Badge> : <span className="text-gray-400">—</span>;
    case 'minutes_badge':
      return v != null ? <Badge variant="outline">{v} min</Badge> : <span className="text-gray-400">—</span>;
    case 'radius_km':
      return v != null ? <Badge variant="outline">{v} km</Badge> : <span className="text-gray-400">—</span>;
    case 'commitment_badge':
      return v != null ? <Badge variant="outline">Min {v} month{v === 1 ? '' : 's'}</Badge> : <span className="text-gray-400">—</span>;
    case 'lead_days':
      return v != null ? <span>Starts in {v} day{v === 1 ? '' : 's'}</span> : <span className="text-gray-400">—</span>;
    case 'time_badge':
      return v ? <Badge variant="outline">{v}</Badge> : <span className="text-gray-400">—</span>;
    case 'number':
      return v != null ? <span>{v}</span> : <span className="text-gray-400">—</span>;
    case 'badge':
    default:
      return v != null && v !== '' ? <span>{String(v)}</span> : <span className="text-gray-400">—</span>;
  }
};

// ---------------------------------------------------------------------------
// RIGHT column — customer decision UI
// ---------------------------------------------------------------------------
export const BuyGuardDecisions = ({ listingId }) => {
  const [config, setConfig] = useState(null);
  const [pincode, setPincode] = useState('');
  const [locality, setLocality] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');

  useEffect(() => {
    if (!listingId) return;
    let ok = true;
    fetchCheckoutConfig(listingId).then(c => { if (ok) setConfig(c); });
    return () => { ok = false; };
  }, [listingId]);

  const pickerFields = useMemo(() => {
    if (!config?.customer_decision_fields) return [];
    // Fields with ui=locality_dropdown, pincode_check, select — i.e. customer picks.
    return config.customer_decision_fields.filter(f =>
      ['locality_dropdown', 'pincode_check', 'select', 'day_chips'].includes(f.ui)
      && f.show !== false
    );
  }, [config]);

  if (!config) return null;

  const hasAnything =
    (config.serviceable_localities || []).length > 0 ||
    (config.delivery_days || []).length > 0 ||
    (config.delivery_time_slots || []).length > 0 ||
    config.requires_state_check ||
    pickerFields.length > 0;

  if (!hasAnything) return null;

  return (
    <Card className="mt-4 border-indigo-200 bg-indigo-50/30" data-testid="buy-guard-decisions">
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-indigo-100">
          <Eye className="h-4 w-4 text-indigo-600" />
          <p className="text-sm font-semibold text-indigo-900">Customer Decision Fields</p>
          <Badge variant="outline" className="text-[10px] bg-white">As customers see</Badge>
        </div>

        {/* Hyperlocal locality picker */}
        {(config.serviceable_localities || []).length > 0 && (
          <div data-testid="buy-guard-locality">
            <label className="text-xs uppercase tracking-wide text-gray-600">
              <MapPin className="inline h-3 w-3 mr-1" />
              Select delivery area
            </label>
            <select
              value={locality}
              onChange={e => setLocality(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              data-testid="buy-guard-locality-select"
            >
              <option value="">— Choose your area —</option>
              {config.serviceable_localities.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}

        {/* Pincode check for selected-cities / pan-india with pincode filter */}
        {!config.serviceable_localities?.length && config.coverage_type === 'selected_cities' && (
          <div data-testid="buy-guard-pincode">
            <label className="text-xs uppercase tracking-wide text-gray-600">
              <MapPin className="inline h-3 w-3 mr-1" /> Check pincode
            </label>
            <div className="flex gap-2 mt-1">
              <Input
                type="text" inputMode="numeric" maxLength={6}
                placeholder="e.g. 400001"
                value={pincode}
                onChange={e => setPincode(e.target.value.replace(/\D/g, ''))}
                data-testid="buy-guard-pincode-input"
              />
              <Button size="sm" disabled={pincode.length !== 6} data-testid="buy-guard-pincode-check">
                Check
              </Button>
            </div>
          </div>
        )}

        {/* Delivery days chip multi-select */}
        {(config.delivery_days || []).length > 0 && (
          <div data-testid="buy-guard-days">
            <label className="text-xs uppercase tracking-wide text-gray-600">
              <Calendar className="inline h-3 w-3 mr-1" /> Delivery days
            </label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {config.delivery_days.map(d => {
                const active = selectedDays.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    data-active={active ? '1' : '0'}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                    }`}
                    onClick={() => setSelectedDays(s =>
                      active ? s.filter(x => x !== d) : [...s, d]
                    )}
                    data-testid={`buy-guard-day-${d}`}
                  >
                    {d.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Time slots — iter-215 P2 feedback: customer-selectable chips */}
        {(config.delivery_time_slots || []).length > 0 && (
          <div data-testid="buy-guard-slots">
            <label className="text-xs uppercase tracking-wide text-gray-600">
              <Clock className="inline h-3 w-3 mr-1" /> Time slots
            </label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {config.delivery_time_slots.map(s => {
                const active = selectedSlot === s;
                return (
                  <button
                    key={s}
                    type="button"
                    data-active={active ? '1' : '0'}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                    }`}
                    onClick={() => setSelectedSlot(active ? '' : s)}
                    data-testid={`buy-guard-slot-${s}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tier-2 state-check notice */}
        {config.requires_state_check && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex gap-2" data-testid="buy-guard-tier2">
            <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              <strong>Tier-2 vendor:</strong> a state-by-state GST check runs at checkout.
              Some states may be restricted — customers get a clear message on the storefront.
            </div>
          </div>
        )}

        {/* Lead-time badge */}
        {(config.minimum_lead_time_days || 0) > 0 && (
          <Row label="Minimum lead time" testid="buy-guard-lead-time">
            <Badge variant="outline">{config.minimum_lead_time_days} day(s)</Badge>
          </Row>
        )}

        {/* Other decision fields (select + day_chips already above) */}
        {pickerFields
          .filter(f => !['locality_dropdown', 'pincode_check', 'day_chips'].includes(f.ui))
          .map(f => (
            <Row key={f.key} label={f.label} testid={`buy-guard-f-${f.key}`}>
              {renderFieldValue(f)}
            </Row>
          ))}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// LEFT column — info tabs (inclusions / exclusions / shipping / policies)
// ---------------------------------------------------------------------------
export const BuyGuardInfoTabs = ({ listingId }) => {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!listingId) return;
    let ok = true;
    fetchCheckoutConfig(listingId).then(c => { if (ok) setConfig(c); });
    return () => { ok = false; };
  }, [listingId]);

  // Group decision fields into tabs — read-only ones only.
  const groups = useMemo(() => {
    const fields = (config?.customer_decision_fields || []).filter(f =>
      f.show !== false &&
      !['locality_dropdown', 'pincode_check', 'day_chips', 'select'].includes(f.ui)
    );
    const g = { inclusions: [], exclusions: [], shipping: [], policies: [], other: [] };
    for (const f of fields) {
      const grp = (f.group || '').toLowerCase();
      const key = (f.key || '').toLowerCase();
      const label = (f.label || '').toLowerCase();
      // Prefer explicit group, fall back to key/label heuristics so
      // the SSOT inclusions/exclusions rows land in the right tab.
      // NOTE: check exclusions BEFORE inclusions because the string
      // "not included" contains "included".
      if (grp.includes('exclusion') || key === 'exclusions' || label.includes('not included')) {
        g.exclusions.push(f);
      } else if (grp.includes('inclusion') || key === 'inclusions' || label.includes('included')) {
        g.inclusions.push(f);
      } else if (grp.includes('ship') || grp.includes('deliver') || grp.includes('logistic')
                 || key.includes('deliver') || key.includes('shipping') || key.includes('travel')
                 || key.includes('dispatch') || key.includes('lead_time') || key.includes('logistic')) {
        g.shipping.push(f);
      } else if (grp.includes('policy') || grp.includes('terms') || grp.includes('return')
                 || key.includes('cancel') || key.includes('refund') || key.includes('warrant')
                 || key.includes('return') || key.includes('guarantee')) {
        g.policies.push(f);
      } else {
        g.other.push(f);
      }
    }
    return g;
  }, [config]);

  if (!config) return null;

  const tabsToShow = [
    { id: 'inclusions', label: 'Inclusions', icon: Shield, fields: groups.inclusions },
    { id: 'exclusions', label: 'Exclusions', icon: AlertCircle, fields: groups.exclusions },
    { id: 'shipping', label: 'Shipping & Returns', icon: Truck, fields: groups.shipping },
    { id: 'policies', label: 'Policies', icon: Shield, fields: groups.policies },
    { id: 'other', label: 'Details', icon: Eye, fields: groups.other },
  ].filter(t => t.fields.length > 0);

  if (tabsToShow.length === 0) return null;

  return (
    <Card className="mt-6" data-testid="buy-guard-info-tabs">
      <CardContent className="pt-4">
        <div className="mb-3 pb-2 border-b flex items-center gap-2">
          <Eye className="h-4 w-4 text-gray-500" />
          <p className="text-sm font-semibold">What customers see</p>
          <Badge variant="outline" className="text-[10px]">Live from SSOT</Badge>
        </div>
        <Tabs defaultValue={tabsToShow[0].id}>
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${tabsToShow.length}, minmax(0, 1fr))` }}>
            {tabsToShow.map(t => (
              <TabsTrigger
                key={t.id} value={t.id}
                data-testid={`buy-guard-tab-${t.id}`}
              >
                <t.icon className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {tabsToShow.map(t => (
            <TabsContent key={t.id} value={t.id} className="mt-3">
              <div className="divide-y">
                {t.fields.map(f => (
                  <Row key={f.key} label={f.label} testid={`buy-guard-info-${f.key}`}>
                    {renderFieldValue(f)}
                  </Row>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default { BuyGuardDecisions, BuyGuardInfoTabs, clearBuyGuardCache };
